import { usePersistedState } from "@/lib/persistedFilters";
import { useEffect, useState, useCallback } from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Plus, Trash2, Search, FileText, Loader2, Download, Mail, Send, Check, X, CalendarClock, History, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadFromUrl } from "@/lib/download";
import { useAuth } from "@/context/AuthContext";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Opportunities } from "@/components/Opportunities";
import { toast } from "sonner";

const money = (n, cur = "CAD") => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
const PIE = { won: "#74b72e", lost: "#e8453c", pending: "#2196f3" };
const STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  submitted: { label: "Awaiting approval", cls: "bg-amber-100 text-amber-700" },
  approved: { label: "Approved", cls: "bg-indigo-100 text-indigo-700" },
  rejected: { label: "Needs changes", cls: "bg-rose-100 text-rose-700" },
  sent: { label: "Sent to client", cls: "bg-sky-100 text-sky-700" },
  won: { label: "Won", cls: "bg-green-100 text-green-700" },
  lost: { label: "Lost", cls: "bg-red-100 text-red-700" },
  expired: { label: "Expired", cls: "bg-orange-100 text-orange-700" },
};
const OPEN_STATUSES = ["draft", "submitted", "approved", "sent", "rejected"];
const daysUntil = (d) => d ? Math.ceil((new Date(`${String(d).slice(0, 10)}T00:00:00`) - new Date(new Date().toDateString())) / 86400000) : null;
const todayISO = () => new Date().toISOString().slice(0, 10);
const plusDaysISO = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

function Panel({ title, right, children, testid }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between bg-[#33456b] px-4 py-2.5">
        <h3 className="font-heading text-sm font-semibold text-white">{title}</h3>
        {right}
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const EMPTY_QUOTE = { name: "", client_id: "none", currency: "CAD", tax_rate: 5, notes: "", valid_until: "", line_items: [] };
const PRICE_TIERS = [
  { key: "cad_msrp", label: "CAD MSRP" },
  { key: "cad_wholesale", label: "CAD Wholesale" },
  { key: "usd_msrp", label: "USD MSRP" },
  { key: "usd_wholesale", label: "USD Wholesale" },
];
const pricesOf = (it) => it ? {
  cad_msrp: Number(it.cad_msrp) || 0, cad_wholesale: Number(it.cad_wholesale) || 0,
  usd_msrp: Number(it.usd_msrp) || 0, usd_wholesale: Number(it.usd_wholesale) || 0,
} : null;

export default function Quotes() {
  const { has } = useAuth();
  const isApprover = has("quote_builder.edit");
  const [dash, setDash] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = usePersistedState("quotes.search", "");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_QUOTE);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("quotes");

  const loadDash = useCallback(() => api.get("/quotes/dashboard").then((r) => setDash(r.data)).catch(() => setDash(false)), []);
  const loadQuotes = useCallback(() => {
    api.get("/quotes", { params: search ? { search } : {} }).then((r) => setQuotes(r.data)).catch(() => {});
  }, [search]);

  useEffect(() => { loadDash(); }, [loadDash]);
  useEffect(() => { const t = setTimeout(loadQuotes, 250); return () => clearTimeout(t); }, [loadQuotes]);
  useEffect(() => {
    api.get("/quote-customers").then((r) => setClients(r.data)).catch(() => {});
    if (has("inventory.view")) api.get("/items").then((r) => setItems(r.data)).catch(() => {});
  }, [has]);

  const canCreate = has("quote_builder.create");
  const canEdit = has("quote_builder.edit");

  const openNew = () => { setEditing(null); setForm({ ...EMPTY_QUOTE, valid_until: plusDaysISO(30) }); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    const lines = (q.line_items || []).map((l) => {
      const it = items.find((x) => x.item_id === l.item_id);
      return { ...l, prices: pricesOf(it) };
    });
    setForm({ name: q.name, client_id: q.client_id || "none", currency: q.currency, tax_rate: q.tax_rate || 0,
      notes: q.notes || "", valid_until: (q.valid_until || "").slice(0, 10), line_items: lines });
    setOpen(true);
  };

  const addLine = (itemId) => {
    const it = items.find((x) => x.item_id === itemId);
    if (!it) return;
    const prices = pricesOf(it);
    const tier = form.currency === "USD" ? "usd_msrp" : "cad_msrp";
    const unit_price = (prices && prices[tier]) || it.unit_price || 0;
    setForm((f) => ({ ...f, line_items: [...f.line_items, { item_id: it.item_id, name: it.name, description: it.description || "", quantity: 1, unit_price, recurring_price: it.recurring_price || 0, price_tier: tier, prices }] }));
  };
  const setTier = (i, tier) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, price_tier: tier, unit_price: (l.prices && l.prices[tier]) ?? l.unit_price } : l) }));
  const updLine = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => idx === i ? { ...l, [k]: k === "quantity" || k === "unit_price" || k === "recurring_price" ? Number(v) : v } : l) }));
  const rmLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const subtotal = form.line_items.reduce((s, l) => s + l.quantity * l.unit_price, 0);
  const recurring = form.line_items.reduce((s, l) => s + l.quantity * l.recurring_price, 0);
  const tax = subtotal * (Number(form.tax_rate) / 100);
  const total = subtotal + tax;

  const save = async () => {
    if (!form.name.trim()) { toast.error("Quote name is required"); return; }
    setSaving(true);
    const payload = { ...form, client_id: form.client_id === "none" ? null : form.client_id };
    try {
      if (editing) { await api.put(`/quotes/${editing.quote_id}`, payload); toast.success("Quote updated"); }
      else { await api.post("/quotes", payload); toast.success("Quote created"); }
      setOpen(false); loadQuotes(); loadDash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (q, path, body, okMsg) => {
    try {
      await api.post(`/quotes/${q.quote_id}/${path}`, body || {});
      toast.success(okMsg); loadQuotes(); loadDash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const submitQuote = (q) => act(q, "submit", {}, `Quote #${q.number} submitted for approval`);
  const approveQuote = (q) => act(q, "approve", {}, `Quote #${q.number} approved`);
  const markStatus = (q, status) => act(q, "status", { status }, `Quote #${q.number} marked ${status}`);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const doReject = async () => {
    await act(rejectTarget, "reject", { reason: rejectReason }, `Quote #${rejectTarget.number} returned for changes`);
    setRejectTarget(null); setRejectReason("");
  };

  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDate, setExtendDate] = useState("");
  const doExtend = async () => {
    if (!extendDate) { toast.error("Pick a new validity date"); return; }
    await act(extendTarget, "extend", { valid_until: extendDate }, `Quote #${extendTarget.number} extended`);
    setExtendTarget(null); setExtendDate("");
  };

  const [versionsTarget, setVersionsTarget] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const openVersions = async (q) => {
    setVersionsTarget(q); setVersions([]); setLoadingVersions(true);
    try {
      const { data } = await api.get(`/quotes/${q.quote_id}/versions`);
      setVersions(data);
    } catch { toast.error("Could not load version history."); }
    finally { setLoadingVersions(false); }
  };
  const restoreVersion = async (v) => {
    try {
      await api.post(`/quotes/${versionsTarget.quote_id}/versions/${v.version}/restore`);
      toast.success(`Restored to v${v.version}`);
      setVersionsTarget(null); loadQuotes(); loadDash();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const EVENT_LABEL = {
    created: "Created", edited: "Edited", submitted: "Submitted", approved: "Approved",
    rejected: "Returned", sent: "Sent to client", extended: "Extended", won: "Marked won",
    lost: "Marked lost", accepted: "Accepted by client",
  };

  const del = async (q) => {
    try { await api.delete(`/quotes/${q.quote_id}`); toast.success("Quote deleted"); loadQuotes(); loadDash(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const [emailing, setEmailing] = useState(null);
  const downloadPdf = async (q) => {
    const t = toast.loading("Preparing PDF…");
    try { await downloadFromUrl(`/quotes/${q.quote_id}/pdf`, `Quote-${q.number}.pdf`); toast.success("PDF downloaded", { id: t }); }
    catch { toast.error("PDF download failed", { id: t }); }
  };
  const emailQuote = async (q) => {
    setEmailing(q.quote_id);
    try {
      const { data } = await api.post(`/quotes/${q.quote_id}/email`, { base_url: window.location.origin });
      toast.success(`Quote #${q.number} emailed to ${data.sent_to}`);
      loadQuotes();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEmailing(null); }
  };

  const pieData = dash ? [
    { name: "Won", value: dash.summary.won_count, key: "won" },
    { name: "Lost", value: dash.summary.lost_count, key: "lost" },
    { name: "Pending", value: dash.summary.pending_count, key: "pending" },
  ].filter((d) => d.value > 0) : [];
  const qpg = useTablePagination(quotes, 25);

  return (
    <div data-testid="quotes-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Quotes</h1>
          <p className="text-sm text-slate-500">Quotes and opportunity pipeline — build, track and close deals.</p>
        </div>
        {canCreate && view === "quotes" && (
          <Button data-testid="new-quote-button" onClick={openNew} className="bg-teal-500 hover:bg-teal-600 text-white">
            <Plus className="h-4 w-4" /> New Quote
          </Button>
        )}
      </div>
      <div className="mb-5 flex gap-1.5 border-b border-slate-200">
        <button data-testid="quotes-view-tab" onClick={() => setView("quotes")} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${view === "quotes" ? "border-teal-500 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>Quotes</button>
        <button data-testid="opportunities-view-tab" onClick={() => setView("opportunities")} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${view === "opportunities" ? "border-teal-500 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>Opportunities</button>
      </div>

      {view === "opportunities" && (
        <Opportunities clients={clients} quotes={quotes} canCreate={canCreate} canEdit={canEdit} canManage={has("quote_builder.edit")} money={money} />
      )}

      {view === "quotes" && (<>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Panel title="At a Glance" testid="quotes-glance">
          <div className="py-6 text-center">
            <div className="font-heading text-5xl font-extrabold text-slate-900" data-testid="closing-ratio">{dash ? `${dash.closing_ratio}%` : "—"}</div>
            <div className="mt-1 text-xs uppercase tracking-widest text-slate-400">Closing Ratio</div>
          </div>
          {dash && (dash.summary.expiring_count > 0 || dash.summary.expired_count > 0) && (
            <div className="flex justify-center gap-4 border-t border-slate-100 pt-3 text-center text-xs">
              <div data-testid="quotes-expiring-stat"><span className="font-semibold text-amber-600">{dash.summary.expiring_count}</span> <span className="text-slate-400">expiring ≤7d</span></div>
              <div data-testid="quotes-expired-stat"><span className="font-semibold text-orange-600">{dash.summary.expired_count}</span> <span className="text-slate-400">expired</span></div>
            </div>
          )}
        </Panel>

        <Panel title="Quotes Summary" testid="quotes-summary">
          <div className="h-40">
            {pieData.length ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={150}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={0} outerRadius={70}>
                    {pieData.map((d) => <Cell key={d.key} fill={PIE[d.key]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="flex h-full items-center justify-center text-sm text-slate-400">No quotes yet</div>}
          </div>
          <div className="mt-3 grid grid-cols-3 border-t border-slate-100 pt-3 text-center">
            <div><div className="text-xs text-slate-400">Quotes</div><div className="text-sm font-semibold text-blue-600">{dash ? money(dash.summary.quotes_total, "") : "—"}</div></div>
            <div><div className="text-xs text-slate-400">Won</div><div className="text-sm font-semibold text-green-600">{dash ? money(dash.summary.won_total, "") : "—"}</div></div>
            <div><div className="text-xs text-slate-400">Lost</div><div className="text-sm font-semibold text-red-600">{dash ? money(dash.summary.lost_total, "") : "—"}</div></div>
          </div>
        </Panel>

        <Panel title="Operation Data" testid="quotes-operation">
          <div className="h-40">
            {dash ? (
              <ResponsiveContainer width="100%" height="100%" minHeight={150}>
                <AreaChart data={dash.revenue_series} margin={{ left: -18, right: 6, top: 6 }}>
                  <defs>
                    <linearGradient id="qg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2196f3" stopOpacity={0.4} /><stop offset="100%" stopColor="#2196f3" stopOpacity={0} /></linearGradient>
                    <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#74b72e" stopOpacity={0.5} /><stop offset="100%" stopColor="#74b72e" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 10, fill: "#94a3b8" }} interval={5} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="quotes" stroke="#2196f3" strokeWidth={2} fill="url(#qg)" name="Quotes" />
                  <Area type="monotone" dataKey="sales" stroke="#74b72e" strokeWidth={2} fill="url(#sg)" name="Sales" />
                </AreaChart>
              </ResponsiveContainer>
            ) : <div className="flex h-full items-center justify-center text-sm text-slate-400">Loading…</div>}
          </div>
          <div className="mt-2 flex gap-4 text-xs text-slate-500"><span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#2196f3]" /> Quotes</span><span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#74b72e]" /> Sales</span></div>
        </Panel>
      </div>

      <div className="mt-4">
        <Panel title="Recent Quotes" testid="quotes-recent"
          right={<div className="relative w-56"><Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" /><Input data-testid="quotes-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search quotes…" className="h-8 border-white/20 bg-white/10 pl-8 text-white placeholder:text-white/50" /></div>}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-left text-xs uppercase text-slate-400">
                <th className="py-2 pr-3">Owner</th><th className="pr-3">No.</th><th className="pr-3">Name</th><th className="pr-3">Org.</th>
                <th className="pr-3">Quote Name</th><th className="pr-3 text-right">Total</th><th className="pr-3 text-right">Recurring</th>
                <th className="pr-3">Status</th><th></th>
              </tr></thead>
              <tbody>
                {quotes.length === 0 ? (
                  <tr><td colSpan={9} className="py-8 text-center text-slate-400">No quotes found.</td></tr>
                ) : qpg.pageItems.map((q) => (
                  <tr key={q.quote_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`quote-row-${q.quote_id}`}>
                    <td className="py-2.5 pr-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-700">{(q.owner_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</span></td>
                    <td className="pr-3 font-medium text-slate-500">{q.number}</td>
                    <td className="pr-3"><button onClick={() => canEdit && openEdit(q)} className="font-medium text-blue-600 hover:underline">{q.client_name || "—"}</button></td>
                    <td className="pr-3 text-slate-600">{q.company || "—"}</td>
                    <td className="pr-3 text-blue-600">
                      {q.name}
                      {q.version > 0 && <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 align-middle text-[10px] font-semibold text-slate-500" data-testid={`quote-version-${q.quote_id}`}>v{q.version}</span>}
                    </td>
                    <td className="pr-3 text-right font-medium text-slate-800">{money(q.total, q.currency)}</td>
                    <td className="pr-3 text-right text-slate-500">{q.recurring_total ? money(q.recurring_total, q.currency) : "—"}</td>
                    <td className="pr-3">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium ${(STATUS[q.status] || STATUS.draft).cls}`} data-testid={`quote-status-${q.quote_id}`}>
                        {(STATUS[q.status] || { label: q.status }).label}
                      </span>
                      {q.status === "rejected" && q.rejection_reason && <div className="mt-1 max-w-[12rem] text-[11px] italic text-rose-500" title={q.rejection_reason}>“{q.rejection_reason}”</div>}
                      {OPEN_STATUSES.includes(q.status) && q.valid_until && daysUntil(q.valid_until) >= 0 && daysUntil(q.valid_until) <= 7 && (
                        <div className="mt-1 text-[11px] font-medium text-amber-600" data-testid={`quote-expiring-${q.quote_id}`}>
                          Expires in {daysUntil(q.valid_until)}d
                        </div>
                      )}
                      {OPEN_STATUSES.includes(q.status) && q.valid_until && (
                        <div className="mt-0.5 text-[10px] text-slate-400">Valid until {String(q.valid_until).slice(0, 10)}</div>
                      )}
                    </td>
                    <td className="pr-1 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        {canEdit && (q.status === "draft" || q.status === "rejected") && (
                          <Button size="sm" data-testid={`submit-quote-${q.quote_id}`} onClick={() => submitQuote(q)} className="h-7 bg-amber-500 hover:bg-amber-600 px-2 text-xs text-white"><Send className="h-3.5 w-3.5" /> Submit</Button>
                        )}
                        {isApprover && q.status === "submitted" && (
                          <>
                            <Button size="sm" data-testid={`approve-quote-${q.quote_id}`} onClick={() => approveQuote(q)} className="h-7 bg-green-600 hover:bg-green-700 px-2 text-xs text-white"><Check className="h-3.5 w-3.5" /> Approve</Button>
                            <Button size="sm" variant="outline" data-testid={`reject-quote-${q.quote_id}`} onClick={() => { setRejectTarget(q); setRejectReason(""); }} className="h-7 border-rose-300 px-2 text-xs text-rose-600 hover:bg-rose-50"><X className="h-3.5 w-3.5" /> Reject</Button>
                          </>
                        )}
                        {isApprover && q.status === "expired" && (
                          <Button size="sm" data-testid={`extend-quote-${q.quote_id}`} onClick={() => { setExtendTarget(q); setExtendDate(plusDaysISO(30)); }} className="h-7 bg-indigo-600 hover:bg-indigo-700 px-2 text-xs text-white"><CalendarClock className="h-3.5 w-3.5" /> Extend</Button>
                        )}
                        {canEdit && (q.status === "approved" || q.status === "sent") && (
                          <>
                            <button data-testid={`mark-won-${q.quote_id}`} onClick={() => markStatus(q, "won")} title="Mark won" className="rounded px-1.5 py-0.5 text-xs font-medium text-green-600 hover:bg-green-50">Won</button>
                            <button data-testid={`mark-lost-${q.quote_id}`} onClick={() => markStatus(q, "lost")} title="Mark lost" className="rounded px-1.5 py-0.5 text-xs font-medium text-red-500 hover:bg-red-50">Lost</button>
                          </>
                        )}
                        <button data-testid={`download-quote-${q.quote_id}`} onClick={() => downloadPdf(q)} title="Download PDF" className="text-slate-300 hover:text-slate-700"><Download className="h-4 w-4" /></button>
                        <button data-testid={`history-quote-${q.quote_id}`} onClick={() => openVersions(q)} title="Version history" className="text-slate-300 hover:text-slate-700"><History className="h-4 w-4" /></button>
                        <button data-testid={`email-quote-${q.quote_id}`} onClick={() => emailQuote(q)} disabled={emailing === q.quote_id || !(q.status === "approved" || q.status === "sent")} title={(q.status === "approved" || q.status === "sent") ? "Email quote to client" : "Approve the quote before sending"} className="text-slate-300 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-300">
                          {emailing === q.quote_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                        </button>
                        {has("quote_builder.delete") && <button data-testid={`delete-quote-${q.quote_id}`} onClick={() => del(q)} title="Delete" className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-3 pt-3">
            <PageSizeSelect pageSize={qpg.pageSize} setPageSize={qpg.setPageSize} testid="quotes-page-size" />
            <TablePagination {...qpg} testid="quotes-pagination" />
          </div>
        </Panel>
      </div>
      </>)}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-teal-500" /> {editing ? `Edit Quote #${editing.number}` : "New Quote"}</DialogTitle>
            <DialogDescription>Choose a client, add items from your catalog and set pricing.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Quote name</Label><Input data-testid="quote-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Telecom Quote" /></div>
              <div className="space-y-1.5"><Label>Client</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                  <SelectTrigger data-testid="quote-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">— None —</SelectItem>{clients.map((c) => <SelectItem key={c.client_id} value={c.client_id}>{c.name} · {c.company}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="quote-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="CAD">CAD</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Tax rate (%)</Label><Input type="number" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Valid until</Label>
                <Input type="date" data-testid="quote-valid-until" value={form.valid_until} min={todayISO()}
                  disabled={!!editing && !isApprover}
                  onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                {!!editing && !isApprover && <p className="text-[10px] text-slate-400">Only Partner Success or Super Admin can change this.</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Line items</Label>
              <Select value="" onValueChange={addLine}>
                <SelectTrigger data-testid="quote-add-item"><SelectValue placeholder="+ Add item from catalog" /></SelectTrigger>
                <SelectContent>{items.map((it) => <SelectItem key={it.item_id} value={it.item_id}>{it.name} — {money(it.unit_price, it.currency)}</SelectItem>)}</SelectContent>
              </Select>
              {form.line_items.length > 0 && (
                <div className="rounded-lg border border-slate-200">
                  {form.line_items.map((l, i) => {
                    const tiers = PRICE_TIERS.filter((t) => l.prices && Number(l.prices[t.key]) > 0);
                    return (
                    <div key={i} className="border-b border-slate-100 p-2 text-sm last:border-0" data-testid={`quote-line-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium text-slate-700">{l.name}</span>
                        <button className="text-slate-300 hover:text-red-500" onClick={() => rmLine(i)}><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-1.5 grid grid-cols-12 items-center gap-2">
                        {tiers.length > 0 && (
                          <Select value={l.price_tier || ""} onValueChange={(v) => setTier(i, v)}>
                            <SelectTrigger className="col-span-4 h-8" data-testid={`quote-line-tier-${i}`}><SelectValue placeholder="Price tier" /></SelectTrigger>
                            <SelectContent>{tiers.map((t) => <SelectItem key={t.key} value={t.key}>{t.label} · {money(l.prices[t.key])}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                        <Input className={`${tiers.length > 0 ? "col-span-2" : "col-span-3"} h-8`} type="number" value={l.quantity} onChange={(e) => updLine(i, "quantity", e.target.value)} title="Qty" placeholder="Qty" />
                        <Input className={`${tiers.length > 0 ? "col-span-3" : "col-span-4"} h-8`} type="number" value={l.unit_price} onChange={(e) => updLine(i, "unit_price", e.target.value)} title="Unit price" data-testid={`quote-line-price-${i}`} placeholder="Price" />
                        <div className="col-span-3 text-right text-xs font-medium text-slate-600">{money(l.quantity * l.unit_price, "")}</div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium">{money(subtotal, form.currency)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tax ({form.tax_rate}%)</span><span>{money(tax, form.currency)}</span></div>
              <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-semibold"><span>Total</span><span data-testid="quote-total">{money(total, form.currency)}</span></div>
              {recurring > 0 && <div className="flex justify-between text-xs text-slate-400"><span>Recurring / mo</span><span>{money(recurring, form.currency)}</span></div>}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-quote-button" onClick={save} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white">{saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Create quote"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Return quote #{rejectTarget?.number} for changes</DialogTitle>
            <DialogDescription>Let the owner know what needs to change. They can edit and resubmit.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={3} placeholder="e.g. Pricing on line 2 is too low; please adjust the discount." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-reject-button" onClick={doReject} className="bg-rose-600 hover:bg-rose-700 text-white">Return for changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Extend quote #{extendTarget?.number}</DialogTitle>
            <DialogDescription>Set a new validity date. This reactivates the quote so it can be re-sent to the client.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>New valid-until date</Label>
            <Input type="date" data-testid="extend-date-input" value={extendDate} min={todayISO()} onChange={(e) => setExtendDate(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendTarget(null)}>Cancel</Button>
            <Button data-testid="confirm-extend-button" onClick={doExtend} className="bg-indigo-600 hover:bg-indigo-700 text-white">Extend &amp; reactivate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!versionsTarget} onOpenChange={(o) => !o && setVersionsTarget(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-teal-500" /> Version history — Quote #{versionsTarget?.number}</DialogTitle>
            <DialogDescription>Every content change and milestone is captured. Restore copies that version's pricing back into the quote.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1" data-testid="quote-versions-list">
            {loadingVersions ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
            ) : versions.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400">No history yet.</div>
            ) : versions.map((v) => (
              <div key={v.version_id} className="rounded-lg border border-slate-200 p-3" data-testid={`quote-version-row-${v.version}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-700">v{v.version}</span>
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium capitalize text-slate-600">{EVENT_LABEL[v.event] || v.event}</span>
                    <span className="text-xs text-slate-500">by {v.editor_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">{new Date(v.created_at).toLocaleString()}</span>
                    {canEdit && (
                      <Button size="sm" variant="outline" data-testid={`restore-version-${v.version}`} onClick={() => restoreVersion(v)} className="h-7 px-2 text-xs">
                        <RotateCcw className="h-3.5 w-3.5" /> Restore
                      </Button>
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-500">
                  <span>Total: <span className="font-medium text-slate-700">{money(v.snapshot?.total, v.snapshot?.currency)}</span></span>
                  {v.snapshot?.recurring_total > 0 && <span>Recurring/mo: <span className="font-medium text-slate-700">{money(v.snapshot.recurring_total, v.snapshot.currency)}</span></span>}
                  <span>{(v.snapshot?.line_items || []).length} line item(s)</span>
                  {v.snapshot?.valid_until && <span>Valid until {String(v.snapshot.valid_until).slice(0, 10)}</span>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
